import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartItem } from '@/lib/shopify';

interface ShakatahCartStore {
  items: CartItem[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  _hasHydrated: boolean;
  
  // Actions
  addItem: (item: CartItem) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  removeItem: (variantId: string) => void;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  createCheckout: () => Promise<void>;
  syncCart: () => Promise<void>;
  setHasHydrated: (state: boolean) => void;
}

const CART_STORAGE_KEY = 'shakatah-split-cart';

// Simple helper to check checkout markers - called only at runtime, never at module load
function checkForCheckoutMarkers(): boolean {
  try {
    return !!(
      localStorage.getItem('shakatah-checkout-in-progress') ||
      localStorage.getItem('shakatah-split-completed') ||
      sessionStorage.getItem('shakatah-checkout-completed-this-session')
    );
  } catch {
    return false;
  }
}

// Exported for useCartSync to use
export function clearAllCheckoutMarkers(): void {
  console.log('Clearing all checkout markers');
  const markers = [
    'shakatah-checkout-in-progress',
    'shakatah-split-completed',
    'shakatah-pending-session',
    'shakatah-checkout-timestamp'
  ];
  markers.forEach(function(key) { localStorage.removeItem(key); });
  sessionStorage.removeItem('shakatah-checkout-completed-this-session');
  localStorage.removeItem(CART_STORAGE_KEY);
}

// Clear widget-specific cart keys
function clearWidgetCartKeys(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('shakatah-split-cart-') && key !== CART_STORAGE_KEY) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(function(key) {
      console.log('Clearing widget cart key:', key);
      localStorage.removeItem(key);
    });
  } catch (e) {
    console.error('Error clearing widget cart keys:', e);
  }
}

export const useShakatahCartStore = create<ShakatahCartStore>()(
  persist(
    function(set, get) {
      return {
        items: [],
        cartId: null,
        checkoutUrl: null,
        isLoading: false,
        isSyncing: false,
        _hasHydrated: false,

        setHasHydrated: function(state) { set({ _hasHydrated: state }); },

        addItem: function(item) {
          if (checkForCheckoutMarkers()) {
            console.log('Blocking addItem - checkout markers detected');
            return;
          }
          
          const items = get().items;
          const existingItem = items.find(function(i) { return i.variantId === item.variantId; });
          
          if (existingItem) {
            set({
              items: items.map(function(i) {
                return i.variantId === item.variantId
                  ? { ...i, quantity: i.quantity + item.quantity }
                  : i;
              })
            });
          } else {
            set({ items: [...items, item] });
          }
        },

        updateQuantity: function(variantId, quantity) {
          if (quantity <= 0) {
            get().removeItem(variantId);
            return;
          }
          
          set({
            items: get().items.map(function(item) {
              return item.variantId === variantId ? { ...item, quantity: quantity } : item;
            })
          });
        },

        removeItem: function(variantId) {
          set({
            items: get().items.filter(function(item) { return item.variantId !== variantId; })
          });
        },

        clearCart: function() {
          console.log('Clearing shakatah cart completely');
          try {
            localStorage.removeItem(CART_STORAGE_KEY);
            clearWidgetCartKeys();
          } catch (e) {
            console.error('Failed to clear shakatah cart from localStorage:', e);
          }
          set({ items: [], cartId: null, checkoutUrl: null });
        },

        setCartId: function(cartId) { set({ cartId: cartId }); },
        setCheckoutUrl: function(checkoutUrl) { set({ checkoutUrl: checkoutUrl }); },
        setLoading: function(isLoading) { set({ isLoading: isLoading }); },

        createCheckout: async function() {
          const state = get();
          if (state.items.length === 0) return;

          state.setLoading(true);
          try {
            // Dynamic import to avoid module-level execution
            const { createStorefrontCheckout } = await import('@/lib/shopify');
            const result = await createStorefrontCheckout(state.items);
            state.setCheckoutUrl(result.checkoutUrl);
            state.setCartId(result.cartId);
            console.log('Shakatah cart created with ID:', result.cartId);
          } catch (error) {
            console.error('Failed to create Shakatah checkout:', error);
            throw error;
          } finally {
            state.setLoading(false);
          }
        },

        syncCart: async function() {
          const state = get();
          
          if (!state._hasHydrated) {
            console.log('Shakatah store not yet hydrated, skipping sync');
            return;
          }
          
          if (checkForCheckoutMarkers()) {
            console.log('Checkout markers detected in syncCart - clearing cart state');
            state.clearCart();
            return;
          }
          
          console.log('Shakatah syncCart called - items:', state.items.length);
          
          if (state.items.length === 0) {
            console.log('No items in shakatah cart, skipping sync');
            return;
          }
          
          if (state.isSyncing) {
            console.log('Already syncing, skipping');
            return;
          }

          set({ isSyncing: true });
          try {
            const currentPath = window.location.pathname;
            if (currentPath.includes('/shakatah-split') || currentPath.includes('/split/')) {
              const pendingSession = localStorage.getItem('shakatah-pending-session');
              if (!pendingSession) {
                console.log('No pending session, split was created - clearing cart');
                state.clearCart();
              }
            }
          } catch (error) {
            console.error('Failed to sync Shakatah cart:', error);
          } finally {
            set({ isSyncing: false });
          }
        }
      };
    },
    {
      name: CART_STORAGE_KEY,
      storage: createJSONStorage(function() { return localStorage; }),
      partialize: function(state) {
        return { 
          items: state.items, 
          cartId: state.cartId, 
          checkoutUrl: state.checkoutUrl 
        };
      },
      onRehydrateStorage: function() {
        return function(state, error) {
          if (error) {
            console.error('Error rehydrating shakatah cart:', error);
            if (state) state.setHasHydrated(true);
            return;
          }
          
          if (checkForCheckoutMarkers()) {
            console.log('onRehydrateStorage: Checkout markers detected - clearing cart');
            try {
              localStorage.removeItem(CART_STORAGE_KEY);
              clearWidgetCartKeys();
              sessionStorage.removeItem('shakatah-imported-cart-hash');
            } catch (e) {
              console.error('Error clearing storage during rehydration:', e);
            }
            
            if (state) {
              state.items = [];
              state.cartId = null;
              state.checkoutUrl = null;
            }
          } else if (state) {
            console.log('Shakatah cart rehydrated:', state.items?.length || 0, 'items');
          }
          
          if (state) state.setHasHydrated(true);
        };
      },
    }
  )
);
